/**
 * BodySlot — [T149-PR8-CONT-11] body region.
 *
 * Sole owner of:
 *   - `data-testid="widget-body"` (the flex-1 min-h-0 overflow-hidden slot)
 *   - The SWR-style 60% opacity fade when `isRefreshing === true`
 *   - The compatibility-warning banner (multi-warning list, top of body)
 *   - All state overlays via `<ActiveOverlay>` (priority order)
 *
 * The `body` ReactNode is always rendered LAST so that overlays (which use
 * `position: absolute`) layer on top correctly. The fade only applies to
 * the body content — overlays always stay fully opaque so retry/fix
 * buttons remain interactive during a refresh.
 */

import { cn } from '@/lib/utils';
import { CompatibilityWarningBanner } from '@/components/dashboard/CompatibilityWarningBanner';
import type { BodyState } from './types';
import { ActiveOverlay } from './Overlays';

export type BodySlotProps = BodyState;

/**
 * Body region. Renders the `body` ReactNode plus the highest-priority
 * overlay (error / timeout / no-data) and the compatibility-warning
 * banner above the body content.
 */
export function BodySlot(body: BodySlotProps) {
  const {
    id: _id,
    body: bodyNode,
    isRefreshing,
    compatibilityWarnings,
  } = body;

  return (
    <div
      data-testid="widget-body"
      className={cn(
        'flex-1 min-h-0 overflow-hidden px-3 pb-2 relative',
        isRefreshing && 'opacity-60 transition-opacity',
      )}
    >
      {compatibilityWarnings && compatibilityWarnings.length > 0 ? (
        <CompatibilityWarningBanner warnings={compatibilityWarnings} />
      ) : null}

      <ActiveOverlay {...body} />

      {bodyNode}
    </div>
  );
}
